import React, { useState, useEffect, useReducer } from "react";
import { useContext } from "react";
import { toast } from "react-toastify";
import openSocket from "../../services/socket-io";

import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import Checkbox from "@material-ui/core/Checkbox";
import Avatar from "@material-ui/core/Avatar";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import IconButton from "@material-ui/core/IconButton";
import SearchIcon from "@material-ui/icons/Search";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import Typography from "@material-ui/core/Typography";
import Divider from "@material-ui/core/Divider";
import CircularProgress from "@material-ui/core/CircularProgress";

import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";
import PersonAddIcon from "@material-ui/icons/PersonAdd";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import UserModal from "../../components/UserModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { menuPermissionOptions } from "../../layout/MainListItems";

const reducer = (state, action) => {
  if (action.type === "LOAD_USERS") {
    const users = action.payload;
    const newUsers = [];

    users.forEach((user) => {
      const userIndex = state.findIndex((u) => u.id === user.id);
      if (userIndex !== -1) {
        state[userIndex] = user;
      } else {
        newUsers.push(user);
      }
    });

    return [...state, ...newUsers];
  }

  if (action.type === "UPDATE_USERS") {
    const user = action.payload;
    const userIndex = state.findIndex((u) => u.id === user.id);

    if (userIndex !== -1) {
      state[userIndex] = user;
      return [...state];
    } else {
      return [user, ...state];
    }
  }

  if (action.type === "DELETE_USER") {
    const userId = action.payload;

    const userIndex = state.findIndex((u) => u.id === userId);
    if (userIndex !== -1) {
      state.splice(userIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const formatProfile = profile => {
  if (profile === "owner") return "Dueño";
  if (profile === "superadmin") return "Superadministrador";
  return profile || "-";
};

const parsePermissions = permissions => {
  if (Array.isArray(permissions)) return permissions;

  if (typeof permissions === "string" && permissions.trim()) {
    try {
      const parsed = JSON.parse(permissions);
      return Array.isArray(parsed) ? parsed : null;
    } catch {
      return null;
    }
  }

  return null;
};

const useStyles = makeStyles((theme) => ({
  headerContent: {
    display: "flex",
    alignItems: "center",
    width: "100%",
    [theme.breakpoints.down("xs")]: {
      alignItems: "stretch",
      flexDirection: "column",
      gap: theme.spacing(1),
    },
  },
  headerButtons: {
    [theme.breakpoints.down("xs")]: {
      display: "grid",
      gridTemplateColumns: "1fr",
      gap: theme.spacing(1),
      marginLeft: 0,
      "& > *": {
        margin: 0,
      },
      "& .MuiTextField-root": {
        width: "100%",
      },
      "& .MuiButton-root": {
        minHeight: 42,
      },
    },
  },
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "auto",
    margin: 20,
    ...theme.scrollbarStyles,
    [theme.breakpoints.down("xs")]: {
      margin: theme.spacing(1),
      padding: theme.spacing(1),
      border: 0,
      boxShadow: "none",
      backgroundColor: "transparent",
    },
  },
  bulkActions: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(1, 0.5),
    flexWrap: "wrap",
    [theme.breakpoints.down("xs")]: {
      padding: theme.spacing(1, 0),
      alignItems: "stretch",
      "& .MuiButton-root": {
        flex: "1 1 auto",
      },
    },
  },
  bulkActionsText: {
    fontWeight: 600,
    marginRight: "auto",
  },
  tableWrapper: {
    [theme.breakpoints.down("xs")]: {
      display: "none",
    },
  },
  permissionsSection: {
    marginTop: theme.spacing(3),
    borderTop: `1px solid ${theme.palette.divider}`,
    paddingTop: theme.spacing(2),
  },
  permissionsTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(1),
  },
  permissionsTableWrapper: {
    overflowX: "auto",
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 6,
  },
  permissionsTable: {
    minWidth: 980,
  },
  permissionsUserCell: {
    position: "sticky",
    left: 0,
    zIndex: 2,
    minWidth: 190,
    maxWidth: 240,
    backgroundColor: theme.palette.background.paper,
    boxShadow: "1px 0 0 rgba(0, 0, 0, 0.08)",
  },
  permissionsHeaderCell: {
    minWidth: 116,
    whiteSpace: "nowrap",
    fontWeight: 600,
  },
  permissionsUserName: {
    fontWeight: 600,
    lineHeight: 1.25,
  },
  permissionsUserMeta: {
    color: theme.palette.text.secondary,
    fontSize: 12,
    lineHeight: 1.3,
  },
  permissionsUserInfo: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    minWidth: 0,
  },
  userAvatar: {
    width: 40,
    height: 40,
    fontSize: 16,
  },
  mobileCards: {
    display: "none",
    [theme.breakpoints.down("xs")]: {
      display: "grid",
      gap: theme.spacing(1.5),
    },
  },
  mobileCard: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    backgroundColor: theme.palette.background.paper,
    padding: theme.spacing(1.5),
  },
  mobileCardHeader: {
    display: "grid",
    gridTemplateColumns: "auto 1fr auto",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  mobileName: {
    fontWeight: 600,
    lineHeight: 1.25,
    wordBreak: "break-word",
  },
  mobileMeta: {
    color: theme.palette.text.secondary,
    fontSize: 13,
    lineHeight: 1.35,
    wordBreak: "break-word",
  },
  mobileCardGrid: {
    display: "grid",
    gap: theme.spacing(1.25),
    marginTop: theme.spacing(1.25),
  },
  mobileField: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    minHeight: 32,
  },
  mobileLabel: {
    color: theme.palette.text.secondary,
    fontSize: 12,
    fontWeight: 600,
    textTransform: "uppercase",
    whiteSpace: "nowrap",
  },
  mobileValue: {
    minWidth: 0,
    textAlign: "right",
    wordBreak: "break-word",
  },
  mobileActions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: theme.spacing(0.5),
  },
}));

const Users = () => {
  const classes = useStyles();
  const { user: loggedInUser } = useContext(AuthContext);

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [deletingUser, setDeletingUser] = useState(null);
  const [userModalOpen, setUserModalOpen] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [users, dispatch] = useReducer(reducer, []);
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [savingPermission, setSavingPermission] = useState("");
  const [principalAdminId, setPrincipalAdminId] = useState(null);

  const userMatchesSearch = (user, term) => {
    const search = String(term || "").trim().toLowerCase();
    if (!search) return true;

    return [
      user?.name,
      user?.email,
      user?.profile,
    ].some(value => String(value || "").toLowerCase().includes(search));
  };

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
    setSelectedUserIds([]);
  }, [searchParam]);

  useEffect(() => {
    const userIds = new Set(users.map(user => String(user.id)));
    setSelectedUserIds(prevState =>
      prevState.filter(userId => userIds.has(String(userId)))
    );
  }, [users]);

  useEffect(() => {
    setLoading(true);
    const fetchUsers = async () => {
      try {
        const { data } = await api.get("/users/", {
          params: { searchParam, pageNumber },
        });
        dispatch({ type: "LOAD_USERS", payload: data.users });
        setPrincipalAdminId(data.principalAdminId || null);
        setHasMore(data.hasMore);
        setLoading(false);
      } catch (err) {
        toastError(err);
      }
    };
    fetchUsers();
  }, [searchParam, pageNumber]);

  useEffect(() => {
    const socket = openSocket();

    socket.on("user", (data) => {
      if (data.action === "update" || data.action === "create") {
        if (userMatchesSearch(data.user, searchParam)) {
          dispatch({ type: "UPDATE_USERS", payload: data.user });
        } else if (data.action === "update") {
          dispatch({ type: "DELETE_USER", payload: +data.user.id });
        }
      }

      if (data.action === "delete") {
        dispatch({ type: "DELETE_USER", payload: +data.userId });
      }
    });

    return () => {
      socket.disconnect();
    };
  }, [searchParam]);

  const handleOpenUserModal = () => {
    setSelectedUser(null);
    setUserModalOpen(true);
  };

  const handleCloseUserModal = () => {
    setSelectedUser(null);
    setUserModalOpen(false);
  };

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setUserModalOpen(true);
  };

  const handleDeleteUser = async (userId) => {
    try {
      await api.delete(`/users/${userId}`);
      toast.success(i18n.t("users.toasts.deleted"));
    } catch (err) {
      toastError(err);
    }
    setDeletingUser(null);
    setSearchParam("");
    setPageNumber(1);
  };

  const visibleUsers =
    loggedInUser?.profile === "superadmin"
      ? users
      : users.filter(user => user.profile !== "superadmin");

  const companyPermissions = parsePermissions(loggedInUser?.company?.menuPermissions);
  const allPermissionValues = menuPermissionOptions.map(option => option.value);
  const visiblePermissionOptions = menuPermissionOptions.filter(option =>
    !Array.isArray(companyPermissions) ||
    companyPermissions.includes(option.value)
  );

  const getUserPermissions = user => {
    if (["superadmin", "owner"].includes(user.profile)) return allPermissionValues;

    const userPermissions = parsePermissions(user.menuPermissions);
    const basePermissions = Array.isArray(userPermissions)
      ? userPermissions
      : Array.isArray(companyPermissions)
        ? companyPermissions
        : allPermissionValues;

    return basePermissions.filter(permission =>
      visiblePermissionOptions.some(option => option.value === permission)
    );
  };

  const userRoleCanUsePermission = (user, permission) => {
    const option = menuPermissionOptions.find(item => item.value === permission);
    if (!option?.adminOnly) return true;
    return ["admin", "superadmin", "owner"].includes(user.profile);
  };

  const userHasPermission = (user, permission) =>
    userRoleCanUsePermission(user, permission) &&
    getUserPermissions(user).includes(permission);

  const canEditUserPermissions = user =>
    !["superadmin", "owner"].includes(user.profile) &&
    !(
      user.profile === "admin" &&
      Number(loggedInUser?.id) !== Number(principalAdminId)
    );

  const canManageUser = user =>
    !(
      user.profile === "admin" &&
      Number(loggedInUser?.id) !== Number(principalAdminId) &&
      Number(loggedInUser?.id) !== Number(user.id)
    );

  const allUsersSelected =
    visibleUsers.filter(canManageUser).length > 0 &&
    visibleUsers
      .filter(canManageUser)
      .every(user => selectedUserIds.includes(String(user.id)));
  const selectedUserCount = selectedUserIds.filter(userId => {
    const user = visibleUsers.find(item => Number(item.id) === Number(userId));
    return user ? canManageUser(user) : false;
  }).length;

  const toggleUserSelection = userId => {
    const user = visibleUsers.find(item => Number(item.id) === Number(userId));
    if (user && !canManageUser(user)) return;

    const normalizedId = String(userId);
    setSelectedUserIds(prevState =>
      prevState.includes(normalizedId)
        ? prevState.filter(id => id !== normalizedId)
        : [...prevState, normalizedId]
    );
  };

  const toggleAllUsersSelection = () => {
    if (allUsersSelected) {
      setSelectedUserIds([]);
      return;
    }

      setSelectedUserIds(
        visibleUsers.filter(canManageUser).map(user => String(user.id))
      );
  };

  const handleDeleteSelectedUsers = async () => {
    try {
      await Promise.all(
        selectedUserIds
          .filter(userId => {
            const user = visibleUsers.find(item => Number(item.id) === Number(userId));
            return user ? canManageUser(user) : false;
          })
          .map(userId => api.delete(`/users/${userId}`))
      );
      selectedUserIds.forEach(userId =>
        dispatch({ type: "DELETE_USER", payload: Number(userId) })
      );
      setSelectedUserIds([]);
      toast.success(i18n.t("users.toasts.deleted"));
    } catch (err) {
      toastError(err);
    } finally {
      setBulkDeleteOpen(false);
      setSearchParam("");
      setPageNumber(1);
    }
  };

  const handleToggleUserPermission = async (user, permission) => {
    if (!canEditUserPermissions(user)) return;

    const currentPermissions = getUserPermissions(user);
    const nextPermissions = currentPermissions.includes(permission)
      ? currentPermissions.filter(item => item !== permission)
      : [...currentPermissions, permission];
    const savingKey = `${user.id}-${permission}`;

    setSavingPermission(savingKey);
    dispatch({
      type: "UPDATE_USERS",
      payload: { ...user, menuPermissions: nextPermissions },
    });

    try {
      const { data } = await api.put(`/users/${user.id}`, {
        menuPermissions: nextPermissions,
      });
      dispatch({ type: "UPDATE_USERS", payload: data });
      toast.success("Permisos actualizados");
    } catch (err) {
      dispatch({ type: "UPDATE_USERS", payload: user });
      toastError(err);
    } finally {
      setSavingPermission("");
    }
  };

  const loadMore = () => {
    setPageNumber((prevState) => prevState + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  const renderUserActions = user => (
    <>
      <IconButton
        size="small"
        disabled={!canManageUser(user)}
        onClick={() => handleEditUser(user)}
      >
        <EditIcon />
      </IconButton>

      <IconButton
        size="small"
        disabled={!canManageUser(user)}
        onClick={() => {
          setConfirmModalOpen(true);
          setDeletingUser(user);
        }}
      >
        <DeleteOutlineIcon />
      </IconButton>
    </>
  );

  const renderMobileUserCard = user => (
    <div className={classes.mobileCard} key={user.id}>
      <div className={classes.mobileCardHeader}>
        <Checkbox
          size="small"
          color="primary"
          disabled={!canManageUser(user)}
          checked={selectedUserIds.includes(String(user.id))}
          onChange={() => toggleUserSelection(user.id)}
          inputProps={{ "aria-label": `Seleccionar ${user.name}` }}
        />
        <div>
          <Typography className={classes.mobileName}>{user.name}</Typography>
          <Typography className={classes.mobileMeta}>{user.email}</Typography>
        </div>
        <div className={classes.mobileActions}>{renderUserActions(user)}</div>
      </div>

      <Divider />

      <div className={classes.mobileCardGrid}>
        <div className={classes.mobileField}>
          <span className={classes.mobileLabel}>
            {i18n.t("users.table.profile")}
          </span>
          <Typography variant="body2" className={classes.mobileValue}>
            {formatProfile(user.profile)}
          </Typography>
        </div>
        <div className={classes.mobileField}>
          <span className={classes.mobileLabel}>
            {i18n.t("users.table.connection")}
          </span>
          <Typography variant="body2" className={classes.mobileValue}>
            {user.connection?.name || "-"}
          </Typography>
        </div>
      </div>
    </div>
  );

  return (
    <MainContainer>
      <ConfirmationModal
        title={
          deletingUser &&
          `${i18n.t("users.confirmationModal.deleteTitle")} ${
            deletingUser.name
          }?`
        }
        open={confirmModalOpen}
        onClose={setConfirmModalOpen}
        onConfirm={() => handleDeleteUser(deletingUser.id)}
      >
        {i18n.t("users.confirmationModal.deleteMessage")}
      </ConfirmationModal>
      <ConfirmationModal
        title={`Eliminar ${selectedUserCount} usuario${
          selectedUserCount === 1 ? "" : "s"
        }?`}
        open={bulkDeleteOpen}
        onClose={setBulkDeleteOpen}
        onConfirm={handleDeleteSelectedUsers}
      >
        {i18n.t("users.confirmationModal.deleteMessage")}
      </ConfirmationModal>
      <UserModal
        open={userModalOpen}
        onClose={handleCloseUserModal}
        aria-labelledby="form-dialog-title"
        userId={selectedUser && selectedUser.id}
      />
      <MainHeader>
        <div className={classes.headerContent}>
          <Title>{i18n.t("users.title")}</Title>
          <MainHeaderButtonsWrapper className={classes.headerButtons}>
            <TextField
              placeholder={i18n.t("contacts.searchPlaceholder")}
              type="search"
              value={searchParam}
              onChange={handleSearch}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon style={{ color: "gray" }} />
                  </InputAdornment>
                ),
              }}
            />
            <Button
              variant="contained"
              color="primary"
              startIcon={<PersonAddIcon />}
              onClick={handleOpenUserModal}
            >
              {i18n.t("users.buttons.add")}
            </Button>
          </MainHeaderButtonsWrapper>
        </div>
      </MainHeader>
      <Paper
        className={classes.mainPaper}
        variant="outlined"
        onScroll={handleScroll}
      >
        {visibleUsers.length > 0 && (
          <div className={classes.bulkActions}>
            <Checkbox
              size="small"
              color="primary"
              checked={allUsersSelected}
              indeterminate={selectedUserCount > 0 && !allUsersSelected}
              onChange={toggleAllUsersSelection}
              inputProps={{ "aria-label": "Seleccionar usuarios" }}
            />
            <Typography variant="body2" className={classes.bulkActionsText}>
              {selectedUserCount
                ? `${selectedUserCount} seleccionado${
                    selectedUserCount === 1 ? "" : "s"
                  }`
                : "Seleccionar usuarios"}
            </Typography>
            {selectedUserCount > 0 && (
              <>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => setSelectedUserIds([])}
                >
                  Limpiar
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  color="secondary"
                  startIcon={<DeleteOutlineIcon />}
                  onClick={() => setBulkDeleteOpen(true)}
                >
                  Eliminar
                </Button>
              </>
            )}
          </div>
        )}
        <div className={classes.mobileCards}>
          {visibleUsers.map(renderMobileUserCard)}
          {loading && <CircularProgress size={24} />}
        </div>

        <Table size="small" className={classes.tableWrapper}>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" />
              <TableCell padding="checkbox" />
              <TableCell align="center">{i18n.t("users.table.name")}</TableCell>
              <TableCell align="center">
                {i18n.t("users.table.email")}
              </TableCell>
              <TableCell align="center">
                {i18n.t("users.table.profile")}
              </TableCell>
              <TableCell align="center">
                {i18n.t("users.table.connection")}
              </TableCell>              
              <TableCell align="center">
                {i18n.t("users.table.actions")}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <>
              {visibleUsers.map((user) => (
                <TableRow key={user.id}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      size="small"
                      color="primary"
                      disabled={!canManageUser(user)}
                      checked={selectedUserIds.includes(String(user.id))}
                      onChange={() => toggleUserSelection(user.id)}
                      inputProps={{ "aria-label": `Seleccionar ${user.name}` }}
                    />
                  </TableCell>
                  <TableCell style={{ paddingRight: 0 }}>
                    <Avatar src={user.profilePicUrl} className={classes.userAvatar}>
                      {user.name?.[0]}
                    </Avatar>
                  </TableCell>
                  <TableCell align="center">{user.name}</TableCell>
                  <TableCell align="center">{user.email}</TableCell>
                  <TableCell align="center">{formatProfile(user.profile)}</TableCell>
                  <TableCell align="center">{user.connection?.name}</TableCell>
                  <TableCell align="center">
                    {renderUserActions(user)}
                  </TableCell>
                </TableRow>
              ))}
              {loading && <TableRowSkeleton avatar columns={5} />}
            </>
          </TableBody>
        </Table>
        {visibleUsers.length > 0 && (
          <div className={classes.permissionsSection}>
            <Typography
              component="h2"
              variant="h6"
              color="primary"
              className={classes.permissionsTitle}
            >
              Permisos de menú
            </Typography>
            <div className={classes.permissionsTableWrapper}>
              <Table size="small" className={classes.permissionsTable}>
                <TableHead>
                  <TableRow>
                    <TableCell className={classes.permissionsUserCell}>
                      Usuario
                    </TableCell>
                    {visiblePermissionOptions.map(permission => (
                      <TableCell
                        key={permission.value}
                        align="center"
                        className={classes.permissionsHeaderCell}
                      >
                        {permission.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visibleUsers.map(user => (
                    <TableRow key={`permissions-${user.id}`}>
                      <TableCell className={classes.permissionsUserCell}>
                        <div className={classes.permissionsUserInfo}>
                          <Avatar src={user.profilePicUrl} className={classes.userAvatar}>
                            {user.name?.[0]}
                          </Avatar>
                          <div>
                            <Typography className={classes.permissionsUserName}>
                              {user.name}
                            </Typography>
                            <Typography className={classes.permissionsUserMeta}>
                              {formatProfile(user.profile)}
                            </Typography>
                          </div>
                        </div>
                      </TableCell>
                      {visiblePermissionOptions.map(permission => {
                        const savingKey = `${user.id}-${permission.value}`;
                        const disabled =
                          !userRoleCanUsePermission(user, permission.value) ||
                          !canEditUserPermissions(user) ||
                          savingPermission === savingKey;

                        return (
                          <TableCell key={permission.value} align="center">
                            <Checkbox
                              size="small"
                              color="primary"
                              checked={userHasPermission(user, permission.value)}
                              disabled={disabled}
                              onChange={() =>
                                handleToggleUserPermission(user, permission.value)
                              }
                              inputProps={{
                                "aria-label": `${permission.label} para ${user.name}`,
                              }}
                            />
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        )}
      </Paper>
    </MainContainer>
  );
};

export default Users;

